import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * Cài đặt cá nhân của tài khoản đăng nhập, lưu trong cột `extra_data` (JSON) của user.
 * - Đọc: `extra_data` trả về kèm GET /api/v1/be/profile (RefreshAuth lưu vào localStorage "user").
 * - Ghi: gửi TOÀN BỘ object extra_data (client tự merge trước khi gửi).
 */

export type ExtraData = Record<string, any>;

/** BE có thể trả extra_data dạng object, chuỗi JSON hoặc null -> chuẩn hoá về object. */
export function normalizeExtraData(raw: unknown): ExtraData {
    if (!raw) return {};
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof raw === "object" && !Array.isArray(raw) ? (raw as ExtraData) : {};
}

/** Lấy extra_data đang cache trong localStorage "user" (do login / RefreshAuth ghi). */
export function readCachedExtraData(): ExtraData {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return normalizeExtraData(user?.extra_data);
    } catch {
        return {};
    }
}

/** Ghi lại extra_data vào cache localStorage "user" để F5 là có ngay, không chờ API. */
export function writeCachedExtraData(extra: ExtraData) {
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...user, extra_data: extra }));
    } catch {
        // localStorage bị chặn -> bỏ qua, server vẫn là nguồn chính
    }
}

export async function updateExtraData(extra: ExtraData): Promise<{ status: boolean; msg?: string }> {
    try {
        const token = localStorage.getItem("token") || "";
        const body = new FormData();
        body.append("extra_data", JSON.stringify(extra));

        const response = await fetch(`${API_URL}/api/v1/be/update-profile`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
            body,
        });

        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg || `HTTP error! status: ${response.status}` };
        }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch (error) {
        console.error("Failed to update extra_data:", error);
        return { status: false };
    }
}
